import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import "./Calendar.css";
import { translations } from "../../../../utils/lang";
import { Context } from "../../../../context/Context";

const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

// Parse "31-July-2025" safely
function parseDateKey(dateStr) {
  const [day, monthName, year] = dateStr.split("-");
  const monthIndex = MONTH_NAMES.findIndex(
    (m) => m.toLowerCase() === monthName.toLowerCase(),
  );
  return new Date(parseInt(year), monthIndex, parseInt(day));
}

// Normalize to "YYYY-MM-DD"
function formatDateKeyFromParts(year, month, day) {
  return `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}

const Calendar = ({ data = [] }) => {
  const { lang } = useContext(Context);
  // Map data by date
  const dataMap = useMemo(() => {
    const map = {};
    data.forEach((item) => {
      const d = parseDateKey(item.date);
      if (!isNaN(d)) {
        const key = formatDateKeyFromParts(
          d.getFullYear(),
          d.getMonth(),
          d.getDate(),
        );
        if (!map[key]) map[key] = [];
        map[key].push(item);
      }
    });
    return map;
  }, [data]);

  // Find range of available months
  const [minDate, maxDate] = useMemo(() => {
    if (data.length === 0) return [null, null];
    let min = null,
      max = null;
    data.forEach((item) => {
      const d = parseDateKey(item.date);
      if (!isNaN(d)) {
        if (!min || d < min) min = d;
        if (!max || d > max) max = d;
      }
    });
    return [min, max];
  }, [data]);

  // Default month = latest available
  const initialMonth = useMemo(() => {
    const start = maxDate || new Date();
    return new Date(start.getFullYear(), start.getMonth(), 1);
  }, [maxDate]);

  const [currentMonth, setCurrentMonth] = useState(initialMonth);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const monthYearLabel = currentMonth.toLocaleString("default", {
    month: "long",
    year: "numeric",
  });

  // Generate month options (latest → oldest)
  const monthOptions = useMemo(() => {
    if (!minDate || !maxDate) return [];
    const opts = [];
    let cur = new Date(maxDate.getFullYear(), maxDate.getMonth(), 1);
    const min = new Date(minDate.getFullYear(), minDate.getMonth(), 1);

    while (cur >= min) {
      opts.push(new Date(cur));
      cur = new Date(cur.getFullYear(), cur.getMonth() - 1, 1);
    }
    return opts;
  }, [minDate, maxDate]);

  // Prepare cells data (rebuilt every render; the memos below depend on it by design)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const cells = [];

  const year = currentMonth.getFullYear();
  const month = currentMonth.getMonth();

  // First day of month
  const firstDay = new Date(year, month, 1);
  const startOffset = firstDay.getDay(); // 0=Sun

  // Start from the Sunday BEFORE (or same day if already Sunday)
  const gridStart = new Date(year, month, 1 - startOffset);

  // Always 6 weeks (6 * 7 = 42 cells) → bulletproof calendar
  for (let i = 0; i < 42; i++) {
    const d = new Date(gridStart);
    d.setDate(gridStart.getDate() + i);

    const key = formatDateKeyFromParts(
      d.getFullYear(),
      d.getMonth(),
      d.getDate(),
    );

    const items = dataMap[key] || [];

    const totalCompleted = items.reduce(
      (sum, it) => sum + (it.completed || 0),
      0,
    );
    const totalAmount = items.reduce(
      (sum, it) => sum + (it.completedAmount || 0),
      0,
    );

    cells.push({
      type: "day",
      key,
      dateObj: d,
      dayNumber: d.getDate(),
      isCurrentMonth: d.getMonth() === month,
      hasData: items.length > 0,
      totalCompleted,
      totalAmount,
    });
  }

  // Group cells into weeks (Sun → Sat)
  const weeks = useMemo(() => {
    const rows = [];
    let current = [];

    cells.forEach((c, i) => {
      current.push(c);
      if (current.length === 7) {
        rows.push(current);
        current = [];
      }
    });

    // If last row < 7, pad it
    if (current.length > 0 && current.length < 7) {
      while (current.length < 7) {
        current.push({ type: "empty", key: `pad-${current.length}` });
      }
      rows.push(current);
    }

    return rows;
  }, [cells]);

  // Totals per weekday (0=Sun → 6=Sat)
  const weekdayTotals = useMemo(() => {
    const totals = {
      0: { completed: 0, amount: 0 }, // Sun
      1: { completed: 0, amount: 0 }, // Mon
      2: { completed: 0, amount: 0 },
      3: { completed: 0, amount: 0 },
      4: { completed: 0, amount: 0 },
      5: { completed: 0, amount: 0 },
      6: { completed: 0, amount: 0 },
    };

    cells.forEach((cell) => {
      if (cell.type === "day" && cell.hasData) {
        const dateObj = new Date(cell.key);
        const dayIndex = dateObj.getDay(); // 0–6

        totals[dayIndex].completed += cell.totalCompleted;
        totals[dayIndex].amount += cell.totalAmount;
      }
    });

    return totals;
  }, [cells]);

  function calculateWeekAverage(week) {
    let total = 0;

    week.forEach((day) => {
      total += day.totalCompleted || 0;
    });

    return total;
  }

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <div className="calendar-container">
      {/* Header with dropdown */}
      <div className="calendar-header">
        <div
          ref={dropdownRef}
          className={`month-dropdown ${dropdownOpen ? "down" : ""}`}
          onClick={() => setDropdownOpen(!dropdownOpen)}
        >
          <span className="month-label">
            {translations[lang].filter.month[monthYearLabel.split(" ")[0]]}-
            {monthYearLabel.split(" ")[1]}
          </span>
          <span className="chevron">{dropdownOpen ? "▲" : "▼"}</span>
          {dropdownOpen && (
            <div className="month-options">
              {monthOptions.map((m) => {
                const label = m.toLocaleString("default", {
                  month: "long",
                  year: "numeric",
                });
                const key = `${m.getFullYear()}-${m.getMonth()}`;
                return (
                  <div
                    key={key}
                    className="month-option"
                    onClick={(e) => {
                      e.stopPropagation();
                      setCurrentMonth(m);
                      setDropdownOpen(false);
                    }}
                  >
                    {translations[lang].filter.month[label.split(" ")[0]]}-
                    {label.split(" ")[1]}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Week headers */}
      <div className="calendar-days">
        {translations[lang].days.map((d) => (
          <div key={d} className="calendar-day-label">
            {d}
          </div>
        ))}

        {weeks.map((week, index) => {
          const avg = calculateWeekAverage(week);

          return (
            <React.Fragment key={index}>
              {week.map((cell, cellIndex) => {
                const isSunday = cellIndex === 0; // first day of week

                return (
                  <div
                    key={cell.key}
                    className={`calendar-cell 
    ${!cell.isCurrentMonth ? "faded" : ""} 
    ${isSunday ? "calendar-week-start" : ""}
`}
                  >
                    {/* Circle ONLY on Sunday */}
                    {isSunday && (
                      <div
                        className={`calendar-week-average ${lang === "ar" ? "ar" : ""}`}
                      >
                        {avg === "-" ? "-" : avg.toLocaleString()}
                      </div>
                    )}

                    <div className="day-number">{cell.dayNumber}</div>

                    {cell.hasData ? (
                      <div className="day-info">
                        <div className="day-completed">
                          {cell.totalCompleted}
                        </div>
                        <div className="day-amount">
                          {Number(cell.totalAmount).toLocaleString()}
                        </div>
                      </div>
                    ) : (
                      <div className="day-empty">-</div>
                    )}
                  </div>
                );
              })}
            </React.Fragment>
          );
        })}
        {[0, 1, 2, 3, 4, 5, 6].map((dayIndex) => {
          const t = weekdayTotals[dayIndex];
          const hasData = t.completed > 0;

          return (
            <div className="calendar-week-start" key={dayIndex}>
              <div key={dayIndex} className="calendar-week-average-bottom">
                {hasData ? t.completed : "-"}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default Calendar;
